import { ActivityGameMatchInfo, MatchParticipant } from '../../interface';

export const MATCH_WIN_REASONS = [
    '(RSC) Win by Referee Stops Contest',
    '(PTF) Win by final score',
    '(WDR) Win by Withdrawal',
    '(DSQ) Win by Disqualification',
    '(DQB) Win by Disqualification for unsportsmanlike behavior',
] as const;

export const MATCH_WALKOVER_REASON = '(WDR) Win by Withdrawal';

export type RelatedMatches = {
    nextMatch: ActivityGameMatchInfo | null;
    nextLoserMatch: ActivityGameMatchInfo | null;
    parentMatches: ActivityGameMatchInfo[];
};

export function isByeOrWalkoverState(state?: string): boolean {
    return state === 'bye' || state === 'walkover';
}

export function matchParticipantDisplayName(
    participant?: Pick<MatchParticipant, 'display_name'> | null,
    matchState?: string,
    emptyLabel = 'TBD',
): string {
    if (participant?.display_name) {
        return participant.display_name;
    }
    if (isByeOrWalkoverState(matchState)) {
        return '';
    }
    return emptyLabel;
}

export function isMatchWinner(
    match: { winner_id?: string | null },
    participantId?: string | null,
): boolean {
    return Boolean(match.winner_id && participantId && match.winner_id === participantId);
}

export const REFERENCE_PREVIEW_HINT = 'This is a reference preview only.';

export function isReferencePreviewMatch(match?: { preview?: boolean } | null): boolean {
    return Boolean(match?.preview);
}

export function shouldClearMatchData(currentState?: string, nextState?: string): boolean {
    return (
        ['in_game', 'finish', 'walkover'].includes(currentState || '') &&
        ['wait', 'locked'].includes(nextState || '')
    );
}

export function relatedMatches(
    currentMatch: ActivityGameMatchInfo | null | undefined,
    matches: ActivityGameMatchInfo[],
): RelatedMatches {
    if (!currentMatch) {
        return { nextMatch: null, nextLoserMatch: null, parentMatches: [] };
    }

    return {
        nextMatch: findMatchById(matches, currentMatch.next_match_id),
        nextLoserMatch: findMatchById(matches, currentMatch.next_loser_match_id),
        parentMatches: matches.filter((match) =>
            match.next_match_id === currentMatch.id ||
            match.next_loser_match_id === currentMatch.id
        ),
    };
}

export function matchWinnerReasonSymbol(
    match: Pick<ActivityGameMatchInfo, 'winner_id' | 'reason' | 'state'>,
    participantId?: string | null,
): string {
    if (!isMatchWinner(match, participantId)) {
        return '';
    }
    // A bye is already labeled on the bout. It is not a walkover.
    if (match.state === 'bye') {
        return '';
    }
    if (match.reason) {
        const code = match.reason.match(/\(([A-Za-z]{3})\)/)?.[1];
        if (code) {
            return code.toUpperCase();
        }
    }
    if (match.state === 'walkover') {
        return 'WO';
    }
    return 'PTF';
}

function findMatchById(
    matches: ActivityGameMatchInfo[],
    id?: string | null,
): ActivityGameMatchInfo | null {
    if (!id) {
        return null;
    }
    return matches.find((match) => match.id === id) || null;
}
